/**
 * A RANDOM VISUAL BASELINE for the celestial confluence study.
 *
 * <h2>What this is for</h2>
 *
 * The confluence study marks a few hundred bars out of several thousand. Looked at on a chart,
 * some of those marks land on obvious turns — and that is not evidence of anything, because a few
 * hundred marks scattered at random would also land on some obvious turns. The eye is very good
 * at finding the hits and very bad at counting the misses.
 *
 * <p>So this draws the same NUMBER of marks at random bar indices, in grey, through the same
 * renderer. If the real study is doing something, the two pictures should look different. If they
 * look the same, they are the same. That comparison costs one click and settles a question no
 * amount of staring at the real overlay can.
 *
 * <h2>This is a VISUAL baseline, not a statistical test</h2>
 *
 * Said plainly because the button invites the stronger reading. One random draw is one sample; a
 * real Monte Carlo would re-roll hundreds of times and compare a distribution of some scored
 * quantity, not two screenshots. What this supports is the weaker and still useful claim: "I
 * cannot tell these apart", which is a finding, and "these look clearly different", which is a
 * reason to go and measure properly.
 */

/**
 * HOW MANY MARKS A DRAW PLACES — taken from the study, never from a constant.
 *
 * <h3>Why this is derived and not 586</h3>
 *
 * It WAS 586 = 240 + 346, matching one particular run. On the very next configuration the real
 * study drew 2,064 marks on SPY, so the baseline was less than a third of its size — and a
 * sparser scatter lands on fewer turns for no reason but being sparser. That makes the real
 * study look better than it is, which is the exact illusion this feature exists to dispel.
 *
 * <p>So the sizes come from the overlay the study just drew: the same total, split the same way.
 * The split is by {@code commonBody} — whether the trine and its matched aspect share a body —
 * which is precisely what the Common and Different checkboxes select on, so each of the three
 * filter settings compares like with like.
 *
 * <p>Counted from what was DRAWN rather than from {@code report.matchCount}, because a match
 * whose day falls past the last bar is counted by the report and appears nowhere on the chart.
 * Sizing from the report would overstate the baseline by exactly the marks a reader cannot see.
 *
 * @param overlay the real study's overlay, from {@code celestialAspectOverlay}
 * @return {{total: number, sizeA: number, sizeB: number}} — sizeA is the COMMON half
 */
export function baselineSizesFor(overlay) {
  const sizeA = Math.max(0, Math.floor((overlay && overlay.commonCount) || 0));
  const sizeB = Math.max(0, Math.floor((overlay && overlay.differentCount) || 0));
  return { total: sizeA + sizeB, sizeA, sizeB };
}

/** Its own id, so a draw can be cleared without touching the real study's overlay. */
export const RANDOM_OVERLAY_ID = 'celestial-random-baseline';

/**
 * Grey, and deliberately not any colour the real study uses.
 *
 * <p>The point of the feature is that the two can be told apart in a screenshot taken a week
 * later by somebody who was not here. Neutral grey at reduced weight reads as "scaffolding"
 * rather than as data, which is exactly what it is.
 */
export const RANDOM_COLOR = '#8A8A8A';

/**
 * Draws `count` DISTINCT indices from `[0, barCount - 1]`.
 *
 * <h3>Why the bound is a parameter</h3>
 *
 * Hardcoding it to one symbol's bar count is the defect this signature exists to prevent. The
 * instruments now on this chart run from about 4,000 bars (platinum) to over 12,000 (gold), so a
 * fixed bound would either throw past the end of a short series or, worse, silently confine every
 * draw to the oldest third of a long one — a "random" baseline clustered in the 1980s, which
 * would make the real study look wonderful for no reason at all.
 *
 * <h3>Partial Fisher-Yates, over a sparse map</h3>
 *
 * Rejection sampling into a Set is simpler and is fine while `count << barCount`, but it degrades
 * badly as the two approach each other and does not terminate at all when they are equal. This
 * shuffles only the first `count` positions and stores displaced values in a map, so it is
 * O(count) in both time and memory, uniform, and correct even when `count === barCount`.
 *
 * @param barCount the series length; the draw is over [0, barCount - 1]
 * @param count    how many to draw. CLAMPED to barCount — a series shorter than the requested
 *                 count yields every bar once rather than looping forever looking for more
 * @param rng      injectable for tests; defaults to Math.random
 * @return indices in DRAW order, which is already uniformly shuffled — see splitIndices
 */
export function drawRandomIndices(barCount, count, rng = Math.random) {
  const n = Number.isFinite(barCount) ? Math.floor(barCount) : 0;
  if (n <= 0) return [];

  const want = Math.max(0, Math.min(Math.floor(count), n));
  const displaced = new Map();
  const valueAt = (i) => (displaced.has(i) ? displaced.get(i) : i);

  const out = [];
  for (let i = 0; i < want; i += 1) {
    // Pick from the untouched tail [i, n-1] and swap it to the front.
    const j = i + Math.floor(rng() * (n - i));
    out.push(valueAt(j));
    displaced.set(j, valueAt(i));
  }
  return out;
}

/**
 * Splits a drawn set into two subsets of the requested sizes.
 *
 * <p>Takes a PREFIX of the draw rather than reshuffling, which is correct precisely because
 * {@link drawRandomIndices} returns its results in uniformly shuffled order — every index is
 * equally likely to be in any position, so the first `sizeA` are a uniform random subset. A
 * second shuffle here would be wasted work dressed up as rigour.
 *
 * <p>Sorted on the way out, because the renderer walks bars left to right and an unsorted list
 * produces marks in a jumbled z-order on overlapping bars.
 */
export function splitIndices(indices, sizeA) {
  const all = Array.isArray(indices) ? indices : [];
  const cut = Math.max(0, Math.min(Math.floor(sizeA), all.length));
  const asc = (a, b) => a - b;
  return {
    subsetA: all.slice(0, cut).sort(asc),
    subsetB: all.slice(cut).sort(asc),
  };
}

/**
 * Which subset the current modifier selection should draw.
 *
 * <h3>Neither selected means BOTH, and that mirrors the real study rather than inventing a rule</h3>
 *
 * The backend's {@code TrineMatcher.accepts} returns true when {@code common == different} — so
 * ticking neither box and ticking both are the same filter, "accept every match". The baseline
 * has to agree, or the comparison at that setting would be between the full real set and a
 * 240-mark random one, and the real study would look twice as dense for no reason.
 *
 * @return the combined, A, or B indices — always ascending
 */
export function subsetForModifiers(subsetA, subsetB, modifiers) {
  const a = Array.isArray(subsetA) ? subsetA : [];
  const b = Array.isArray(subsetB) ? subsetB : [];
  const common = !!(modifiers && modifiers.common);
  const different = !!(modifiers && modifiers.different);

  if (common === different) {
    return [...a, ...b].sort((x, y) => x - y);   // both, or neither — the same filter
  }
  return common ? a : b;
}

/**
 * Builds the overlay for a draw, in the SAME shape the real study produces.
 *
 * <p>Same `type`, same point fields, same renderer — so this needs no ECharts change and cannot
 * drift from how the real marks are drawn. If the two were rendered by different code the
 * comparison would be between two drawings as much as between two signals.
 *
 * @param placement 'BARS' or 'RAIL', passed straight through so a draw follows whatever the
 *                  reader has the real study set to
 */
export function randomBaselineOverlay(indices, barData, placement = 'BARS', meta = {}) {
  const dates = (barData && Array.isArray(barData.dates)) ? barData.dates : [];
  const ohlc = (barData && Array.isArray(barData.ohlc)) ? barData.ohlc : [];
  const wanted = Array.isArray(indices) ? indices : [];

  const points = [];
  wanted.forEach((i) => {
    if (!Number.isInteger(i) || i < 0 || i >= dates.length) return;
    const bar = ohlc[i];
    if (!Array.isArray(bar) || !Number.isFinite(bar[3])) return;
    points.push({
      date: dates[i],
      price: bar[3],
      position: 'above',
      // HOLLOW, always. The real study uses a filled triangle for a trading day and a hollow one
      // for a date snapped forward; a hollow grey triangle belongs to neither of those readings
      // and cannot be mistaken for either.
      symbol: 'emptyTriangle',
      symbolRotate: 180,
      color: RANDOM_COLOR,
      // No letter. The real marks carry C/S/O for the aspects they matched, and a random mark
      // has matched nothing — putting a letter on it would be inventing a provenance.
      label: '',
      title: 'Random baseline',
      tooltipLines: [
        `Bar       ${dates[i]}`,
        `Index     ${i} of ${dates.length}`,
        'Source    RANDOM — not a computed aspect',
      ],
      tooltipFooter: 'visual baseline only',
      offsetPx: 14,
      random: true,
    });
  });

  return {
    type: placement === 'RAIL' ? 'aspectRail' : 'pivotMarkers',
    id: RANDOM_OVERLAY_ID,
    placement,
    points,
    color: RANDOM_COLOR,
    label: 'Random baseline',
    source: 'celestialRandomBaseline',
    // Same exception as the study it is a baseline for — and it matters more here, because this
    // card is the one thing that says "Source RANDOM — not a computed aspect". Grey marks a
    // reader cannot interrogate are exactly what this feature must never leave on a chart.
    researchTooltip: true,
    symbol: 'emptyTriangle',
    symbolSize: 10,
    symbolOffsetPx: 14,
    labelFontSize: 10,
    labelFontFamily: 'ui-monospace,SFMono-Regular,Menlo,Consolas,monospace',
    // Faint, so a draw never competes with the price action it is scattered over.
    railLineOpacity: 0.25,
    railLineType: 'dotted',
    drawnCount: points.length,
    requestedCount: wanted.length,
    ...meta,
  };
}

/**
 * The status line for a draw.
 *
 * <p>Reports the real study's count beside the random one every time. It should now always
 * match — the sizes are derived from that very overlay — so the two numbers are printed as a
 * CHECK rather than as a caveat: if they ever diverge, something between the sizing and the
 * drawing has gone wrong and the comparison is void, and that must be visible rather than
 * assumed. The old fixed-count version could be out by a factor of three and said nothing.
 */
export function randomBaselineStatus(overlay, actualCount) {
  if (!overlay || overlay.drawnCount === 0) {
    return 'Random baseline: nothing drawn — the study marked no bars to match';
  }
  const bits = [`RANDOM BASELINE: ${overlay.drawnCount} grey marks`];
  if (overlay.subsetLabel) bits.push(`(${overlay.subsetLabel})`);

  if (Number.isFinite(actualCount)) {
    if (actualCount === overlay.drawnCount) {
      bits.push(`· size-matched to the study's ${actualCount}`);
    } else {
      bits.push(`· the study marks ${actualCount} — NOT size-matched, compare density with care`);
    }
  }
  bits.push('· scaffolding, not computed data');
  return bits.join(' ');
}
